import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks, mainSiteUrl, categories, mobileNavLinks } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileExpandedSection(null);
  }, [location]);

  const isHomePage = location.pathname === '/';

  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection(mobileExpandedSection === section ? null : section);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                {link.name}
              </Link>
            ))}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in max-h-[80vh] overflow-y-auto">
            <div className="py-3 px-2 space-y-1">
              {mobileNavLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-background-100 rounded-lg">
                    <i className={`${link.icon} text-base text-foreground-500`}></i>
                  </div>
                  {link.name}
                </Link>
              ))}

              <div className="border-t border-background-200 my-2"></div>

              <button
                onClick={() => toggleMobileSection('categories')}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-primary-500 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className="ri-grid-line text-base text-primary-500"></i>
                  </div>
                  Kategorie produktów
                </div>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileExpandedSection === 'categories' ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileExpandedSection === 'categories' && (
                <div className="ml-4 space-y-0.5 animate-fade-in">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-md">
                        <i className={`${cat.icon} text-sm text-foreground-400`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200 my-2"></div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-primary-500 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-external-link-line text-base text-primary-500"></i>
                </div>
                Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}